import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';

// Components
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import ReviewDetailPage from './pages/ReviewDetailPage';
import SearchResultsPage from './pages/SearchResultsPage';
import ActivityPage from './pages/ActivityPage';
import FollowersPage from './pages/FollowersPage';
import FollowingPage from './pages/FollowingPage';
import ReviewLikesPage from './pages/ReviewLikesPage';
import ListLikesPage from './pages/ListLikesPage';
import SettingsPage from './pages/SettingsPage';
import ListDetailPage from './pages/ListDetailPage';
import AlbumDetailPage from './pages/AlbumDetailPage';

// Auth Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

// Theme
const theme = {
  colors: {
    primary: '#667eea',
    secondary: '#764ba2',
    background: '#fafafa',
    white: '#ffffff',
    gray: {
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },
    success: '#10b981',
    error: '#ef4444',
    warning: '#f59e0b',
  },
  fonts: {
    primary: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  breakpoints: {
    mobile: '768px',
    tablet: '1024px',
    desktop: '1200px',
  },
};

// Global Styles
const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: ${(props) => props.theme.fonts.primary};
    background: ${(props) => props.theme.colors.background};
    color: ${(props) => props.theme.colors.gray[900]};
    line-height: 1.6;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  button {
    font-family: inherit;
    cursor: pointer;
  }

  input, textarea {
    font-family: inherit;
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  flex: 1;
  padding-top: 80px; // Account for fixed navbar
`;

// Component to handle authenticated routes
const AppRoutes: React.FC = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#6b7280'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <AppContainer>
      {user && <Navbar />}
      <MainContent>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/" replace />} />
          <Route path="/register" element={!user ? <RegisterPage /> : <Navigate to="/" replace />} />
          
          {/* Protected routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          } />
          <Route path="/activity" element={
            <ProtectedRoute>
              <ActivityPage />
            </ProtectedRoute>
          } />
          <Route path="/search" element={
            <ProtectedRoute>
              <SearchResultsPage />
            </ProtectedRoute>
          } />
          <Route path="/users/:username" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/users/:username/followers" element={
            <ProtectedRoute>
              <FollowersPage />
            </ProtectedRoute>
          } />
          <Route path="/users/:username/following" element={
            <ProtectedRoute>
              <FollowingPage />
            </ProtectedRoute>
          } />
          <Route path="/review/:reviewId" element={
            <ProtectedRoute>
              <ReviewDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/review/:reviewId/likes" element={<ReviewLikesPage />} />
          <Route path="/lists/:listId/likes" element={<ListLikesPage />} />
          <Route path="/lists/:listId" element={
            <ProtectedRoute>
              <ListDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/albums/:discogsId" element={
            <ProtectedRoute>
              <AlbumDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={<SettingsPage />} />
          
          {/* Catch all - redirect based on auth status */}
          <Route path="*" element={<Navigate to={user ? "/" : "/login"} replace />} />
        </Routes>
      </MainContent>
    </AppContainer>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <Router>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
